/**
 * Shared Lead API Functions
 *
 * This module provides API functions for shared lead operations
 * with different access levels: view, edit, full
 */

import axiosInstance from './axios.config';

/**
 * Get shared lead details including allowed actions
 * @param {number} shareId - The share ID
 * @returns {Promise} API response with lead data, reports, documents, and allowed_actions
 */
export const getSharedLeadDetails = async (shareId) => {
    const response = await axiosInstance.get(`/leads/shared/${shareId}/details`);
    return response.data;
};

/**
 * Update shared lead details (requires edit or full access)
 * @param {number} shareId - The share ID
 * @param {object} leadData - Updated lead data
 * @returns {Promise} API response with updated lead
 */
export const updateSharedLead = async (shareId, leadData) => {
    const response = await axiosInstance.put(`/leads/shared/${shareId}/update`, leadData);
    return response.data;
};

/**
 * Upload document to shared lead (requires edit or full access)
 * @param {number} shareId - The share ID
 * @param {FormData} formData - Form data containing document and document_type
 * @returns {Promise} API response with uploaded document info
 */
export const uploadSharedDocument = async (shareId, formData) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/documents`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

/**
 * Delete document from shared lead (requires edit or full access)
 * @param {number} shareId - The share ID
 * @param {string} docType - Document type to delete
 * @param {number|null} index - For multi-document types, the index to delete (optional)
 * @returns {Promise} API response
 */
export const deleteSharedDocument = async (shareId, docType, index = null) => {
    let url = `/leads/shared/${shareId}/documents/${docType}`;
    if (index !== null && index !== undefined) {
        url += `?index=${index}`;
    }
    const response = await axiosInstance.delete(url);
    return response.data;
};

/**
 * Fetch new report for shared lead (requires full access)
 * Uses the requesting user's credits
 * @param {number} shareId - The share ID
 * @param {string} reportType - Type of report to fetch (equifax, pan, bank_statement, etc.)
 * @param {object} parameters - Report-specific parameters
 * @returns {Promise} API response with report data and credit info
 */
export const fetchSharedLeadReport = async (shareId, reportType, parameters = {}) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/fetch-report`, {
        report_type: reportType,
        parameters
    });
    return response.data;
};

/**
 * Re-share lead to another user (requires full access)
 * @param {number} shareId - The share ID
 * @param {object} shareData - Share data (share_with, share_type, message, etc.)
 * @returns {Promise} API response with new share info
 */
export const reShareLead = async (shareId, shareData) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/reshare`, shareData);
    return response.data;
};

/**
 * Request to unmask personal details on a shared lead
 * Only the recipient can request unmask
 * @param {number} shareId - The share ID
 * @param {string} message - Optional message explaining why unmask is needed
 * @returns {Promise} API response
 */
export const requestUnmask = async (shareId, message = null) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/request-unmask`, { message });
    return response.data;
};

/**
 * Approve unmask request
 * Only the owner can approve
 * @param {number} shareId - The share ID
 * @returns {Promise} API response
 */
export const approveUnmask = async (shareId) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/approve-unmask`);
    return response.data;
};

/**
 * Reject unmask request
 * Only the owner can reject
 * @param {number} shareId - The share ID
 * @returns {Promise} API response
 */
export const rejectUnmask = async (shareId) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/reject-unmask`);
    return response.data;
};

/**
 * Toggle masking on/off for a share
 * Only the owner can toggle
 * @param {number} shareId - The share ID
 * @param {boolean} maskDetails - Whether to enable masking
 * @returns {Promise} API response
 */
export const toggleMasking = async (shareId, maskDetails) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/toggle-masking`, { mask_details: maskDetails });
    return response.data;
};

/**
 * Get pending unmask requests for the owner
 * @returns {Promise} API response with pending requests
 */
export const getPendingUnmaskRequests = async () => {
    const response = await axiosInstance.get('/leads/unmask-requests');
    return response.data;
};

/**
 * Get shareable users for re-sharing
 * @param {object} filters - Optional filters (search, category, page, limit)
 * @returns {Promise} API response with shareable users list
 */
export const getShareableUsers = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const queryString = params.toString();
    const url = queryString ? `/leads/shareable-users?${queryString}` : '/leads/shareable-users';

    const response = await axiosInstance.get(url);
    return response.data;
};

/**
 * Get user credit balance
 * @returns {Promise} API response with user credits
 */
export const getUserCredits = async () => {
    const response = await axiosInstance.get('/user/credits');
    return response.data;
};

/**
 * Report types configuration for fetch report dialog
 */
export const REPORT_TYPES = {
    equifax: {
        id: 'equifax',
        name: 'Equifax Credit Report',
        icon: 'pi-chart-line',
        description: 'Fetch credit bureau report from Equifax',
        requiredFields: ['pan', 'full_name', 'dob']
    },
    pan: {
        id: 'pan',
        name: 'PAN Verification',
        icon: 'pi-id-card',
        description: 'Verify PAN details',
        requiredFields: ['pan']
    },
    bank_statement: {
        id: 'bank_statement',
        name: 'Bank Statement Analysis',
        icon: 'pi-credit-card',
        description: 'Analyze bank statement',
        requiredFields: ['bank_statement_path']
    },
    employment_mobile: {
        id: 'employment_mobile',
        name: 'Employment History (Mobile)',
        icon: 'pi-briefcase',
        description: 'Get employment history by mobile number',
        requiredFields: ['phone']
    },
    employment_uan: {
        id: 'employment_uan',
        name: 'Employment History (UAN)',
        icon: 'pi-briefcase',
        description: 'Get employment history by UAN',
        requiredFields: ['uan']
    },
    driving_licence: {
        id: 'driving_licence',
        name: 'Driving Licence Verification',
        icon: 'pi-car',
        description: 'Verify driving licence details',
        requiredFields: ['dl_number', 'dob']
    },
    voter_id: {
        id: 'voter_id',
        name: 'Voter ID Verification',
        icon: 'pi-check-square',
        description: 'Verify voter ID details',
        requiredFields: ['voter_id']
    },
    mobile_bureau: {
        id: 'mobile_bureau',
        name: 'Mobile Bureau Lookup',
        icon: 'pi-mobile',
        description: 'Mobile bureau lookup',
        requiredFields: ['phone']
    },
    ca_membership: {
        id: 'ca_membership',
        name: 'CA Membership Verification',
        icon: 'pi-verified',
        description: 'Verify CA membership',
        requiredFields: ['membership_number']
    }
};

/**
 * Document types configuration for upload
 * Maps employment document codes to API document types
 */
export const DOCUMENT_TYPES = [
    { value: 'pan', label: 'PAN Card', code: 'pan_document', multiSelect: false, icon: 'pi-id-card' },
    { value: 'aadhar', label: 'Aadhaar Card', code: 'aadhar_document', multiSelect: false, icon: 'pi-id-card' },
    { value: 'address_proof', label: 'Address Proof', code: 'address_proof', multiSelect: true, icon: 'pi-home' },
    { value: 'bank_statement', label: 'Bank Statement', code: 'bank_statement', multiSelect: true, icon: 'pi-credit-card' },
    { value: 'appointment_letter', label: 'Appointment Letter', code: 'appointment_letter', multiSelect: true, icon: 'pi-briefcase' },
    { value: 'existing_loan_sanction_letters', label: 'Existing Loan Sanction Letters', code: 'existing_loan_sanction_letters', multiSelect: true, icon: 'pi-file' },
    { value: 'cibil_report', label: 'CIBIL Report', code: 'cibil_report', multiSelect: true, icon: 'pi-chart-line' },
    { value: 'itr', label: 'Income Tax Return (3 Years)', code: 'itr', multiSelect: true, icon: 'pi-file-pdf' },
    { value: 'gst', label: 'GST Returns (3 Years)', code: 'gst', multiSelect: true, icon: 'pi-file-excel' },
    { value: 'payslip', label: 'Payslip', code: 'payslip', multiSelect: true, icon: 'pi-wallet' },
    { value: 'professional_certificate', label: 'Professional Certificate', code: 'professional_certificate', multiSelect: true, icon: 'pi-verified' },
    { value: 'rental_agreement', label: 'Rental Agreements', code: 'rental_agreements', multiSelect: true, icon: 'pi-building' },
    { value: 'other', label: 'Other Documents', code: 'other_documents', multiSelect: true, icon: 'pi-folder-open' }
];

/**
 * Map from employment document code to API document type
 */
export const DOCUMENT_CODE_TO_API_TYPE = {
    'pan_document': 'pan',
    'aadhar_document': 'aadhar',
    'address_proof': 'address_proof',
    'bank_statement': 'bank_statement',
    'appointment_letter': 'appointment_letter',
    'existing_loan_sanction_letters': 'existing_loan_sanction_letters',
    'cibil_report': 'cibil_report',
    'itr': 'itr',
    'gst': 'gst',
    'payslip': 'payslip',
    'professional_certificate': 'professional_certificate',
    'rental_agreements': 'rental_agreement',
    'other_documents': 'other'
};

/**
 * Get API document type from employment document code
 */
export const getApiDocumentType = (employmentCode) => {
    return DOCUMENT_CODE_TO_API_TYPE[employmentCode] || employmentCode;
};

/**
 * Get document config by API type
 */
export const getDocumentConfigByApiType = (apiType) => {
    return DOCUMENT_TYPES.find(doc => doc.value === apiType);
};

/**
 * Get document config by employment code
 */
export const getDocumentConfigByCode = (code) => {
    return DOCUMENT_TYPES.find(doc => doc.code === code);
};

/**
 * Get full shared lead details for wizard-style display
 * Returns lead_data, existing_loans, offers, eligibility + share context
 * @param {number} shareId - The share ID
 * @returns {Promise} API response with full lead data and share context
 */
export const getSharedLeadFullDetails = async (shareId) => {
    const response = await axiosInstance.get(`/leads/shared/${shareId}/full-details`);
    return response.data;
};

/**
 * Save stage data for a shared lead (requires edit/full access)
 * @param {number} shareId - The share ID
 * @param {object} stageData - { stage: string, data: object }
 * @returns {Promise} API response
 */
export const saveSharedStageData = async (shareId, stageData) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/save-stage`, stageData);
    return response.data;
};

/**
 * Generate offers for a shared lead (requires edit/full access)
 * @param {number} shareId - The share ID
 * @param {object} data - Offer generation parameters
 * @returns {Promise} API response with generated offers
 */
export const generateSharedOffers = async (shareId, data) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/generate-offers`, data);
    return response.data;
};

/**
 * Check eligibility for a shared lead (requires edit/full access)
 * @param {number} shareId - The share ID
 * @param {object} data - Eligibility check parameters
 * @returns {Promise} API response with eligibility result
 */
export const checkSharedEligibility = async (shareId, data) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/check-eligibility`, data);
    return response.data;
};

/**
 * Select an offer for a shared lead (requires edit/full access)
 * @param {number} shareId - The share ID
 * @param {object} data - { offer_id, selected_offer, offers_count }
 * @returns {Promise} API response
 */
export const selectSharedOffer = async (shareId, data) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/select-offer`, data);
    return response.data;
};

/**
 * Save existing loan for a shared lead (requires edit/full access)
 * @param {number} shareId - The share ID
 * @param {object} loanData - Existing loan data
 * @returns {Promise} API response
 */
export const saveSharedExistingLoan = async (shareId, loanData) => {
    const response = await axiosInstance.post(`/leads/shared/${shareId}/save-existing-loan`, loanData);
    return response.data;
};

/**
 * Delete existing loan for a shared lead (requires edit/full access)
 * @param {number} shareId - The share ID
 * @param {number} loanId - The loan ID to delete
 * @returns {Promise} API response
 */
export const deleteSharedExistingLoan = async (shareId, loanId) => {
    const response = await axiosInstance.delete(`/leads/shared/${shareId}/existing-loan/${loanId}`);
    return response.data;
};

export default {
    getSharedLeadDetails,
    getSharedLeadFullDetails,
    updateSharedLead,
    uploadSharedDocument,
    deleteSharedDocument,
    fetchSharedLeadReport,
    reShareLead,
    // Masking functions
    requestUnmask,
    approveUnmask,
    rejectUnmask,
    toggleMasking,
    getPendingUnmaskRequests,
    // Shared wizard workflow functions
    saveSharedStageData,
    generateSharedOffers,
    checkSharedEligibility,
    selectSharedOffer,
    saveSharedExistingLoan,
    deleteSharedExistingLoan,
    // Other functions
    getShareableUsers,
    getUserCredits,
    REPORT_TYPES,
    DOCUMENT_TYPES,
    DOCUMENT_CODE_TO_API_TYPE,
    getApiDocumentType,
    getDocumentConfigByApiType,
    getDocumentConfigByCode
};
